import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    ReactNode
} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap';

type ConfirmVariant = 'primary' | 'danger';

export interface ConfirmOptions {
    title?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: ConfirmVariant;
}

export interface AlertOptions {
    title?: ReactNode;
    okLabel?: string;
}

interface DialogContextValue {
    /** Yes/No confirmation. Resolves true when confirmed, false when cancelled/dismissed. */
    confirm: (content: ReactNode, opts?: ConfirmOptions) => Promise<boolean>;
    /** OK-only notice. Resolves once dismissed. */
    alert: (content: ReactNode, opts?: AlertOptions) => Promise<void>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

type DialogState = {
    isOpen: boolean;
    kind: 'confirm' | 'alert';
    title: ReactNode;
    content: ReactNode;
    confirmLabel: string;
    cancelLabel: string;
    okLabel: string;
    variant: ConfirmVariant;
    /** Resolves the pending promise. confirm → boolean, alert → void (resolved with false). */
    resolve: (result: boolean) => void;
};

const INITIAL_STATE: DialogState = {
    isOpen: false,
    kind: 'confirm',
    title: 'Confirm',
    content: '',
    confirmLabel: 'Yes',
    cancelLabel: 'No',
    okLabel: 'OK',
    variant: 'primary',
    resolve: () => {}
};

/**
 * App-wide promise-based dialogs. A single <Modal> instance lives here; calling
 * confirm()/alert() opens it and stashes the pending promise's resolve callback in
 * state. This replaces native window.confirm / window.alert so prompts are themed,
 * support rich ReactNode content, and — crucially — are awaitable.
 *
 * Must sit above any consumer of useDialogs. Placing it outside RouterProvider keeps
 * it available to every route (and to a future navigation guard).
 */
export const DialogProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const [state, setState] = useState<DialogState>(INITIAL_STATE);

    const close = useCallback((result: boolean) => {
        setState(prev => {
            prev.resolve(result);
            return {...prev, isOpen: false, resolve: () => {}};
        });
    }, []);

    const confirm = useCallback<DialogContextValue['confirm']>(
        (content, opts = {}) =>
            new Promise<boolean>(resolve => {
                setState({
                    ...INITIAL_STATE,
                    isOpen: true,
                    kind: 'confirm',
                    title: opts.title ?? 'Confirm',
                    content,
                    confirmLabel: opts.confirmLabel ?? 'Yes',
                    cancelLabel: opts.cancelLabel ?? 'No',
                    variant: opts.variant ?? 'primary',
                    resolve
                });
            }),
        []
    );

    const alert = useCallback<DialogContextValue['alert']>(
        (content, opts = {}) =>
            new Promise<void>(resolve => {
                setState({
                    ...INITIAL_STATE,
                    isOpen: true,
                    kind: 'alert',
                    title: opts.title ?? 'Notice',
                    content,
                    okLabel: opts.okLabel ?? 'OK',
                    resolve: () => resolve()
                });
            }),
        []
    );

    const value = useMemo<DialogContextValue>(
        () => ({confirm, alert}),
        [confirm, alert]
    );

    return (
        <DialogContext.Provider value={value}>
            {children}
            <Modal isOpen={state.isOpen} toggle={() => close(false)}>
                <ModalHeader toggle={() => close(false)}>
                    <span>{state.title}</span>
                </ModalHeader>
                <ModalBody>{state.content}</ModalBody>
                <ModalFooter>
                    {state.kind === 'confirm' ? (
                        <>
                            <Button
                                color="secondary"
                                onClick={() => close(false)}
                            >
                                {state.cancelLabel}
                            </Button>
                            <Button
                                color={state.variant}
                                onClick={() => close(true)}
                            >
                                {state.confirmLabel}
                            </Button>
                        </>
                    ) : (
                        <Button color="primary" onClick={() => close(false)}>
                            {state.okLabel}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </DialogContext.Provider>
    );
};

/** Access the app-wide confirm/alert dialogs. Must be used under <DialogProvider>. */
export const useDialogs = (): DialogContextValue => {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error('useDialogs must be used within a DialogProvider');
    }
    return ctx;
};
